<script setup lang="ts">
import type { OfferTermsContent, SignupOffer } from '@promotion/types'
import type { HeroContent } from '@nimbus/types'
import type { StickyConfig } from './Promotion-Terms.vue'
import { computed, onServerPrefetch, usePartner, useSignupPromotions, useUserInfo } from '#imports'

defineOptions({
    name: 'StickyTerms'
})

export interface StickyTermsProps {
    /** Solid background color for the terms band (non-sticky usage). Sticky bands use `stickyConfig.backgroundColor`. */
    backgroundColor?: string
    /** Text + link color. Forwarded to PromotionTerms (`--sticky-terms-color`). */
    textColor?: string
    /** Drop the `.container` width cap so the band bleeds edge-to-edge; the inner legal text stays capped at 1024px. */
    fluid?: boolean
    /** Force the heading to render even for a single term (forwarded to PromotionTerms `showTitle` / `showTermTitle`). */
    showTitle?: boolean
    /** Reset the top margin to 0 (e.g. on sticky bands, which inherit `margin-top: 15px` from PromotionTerms). */
    resetMargin?: boolean
    title?: string
    isLandingPage?: boolean
    heroData?: HeroContent
    forceFullTerms?: boolean
    enableMultiple?: boolean
    stickyConfig?: StickyConfig
}

const props = defineProps<StickyTermsProps>()

const { isFromPartner } = usePartner()
const signupPromotionsQuery = useSignupPromotions()
const { signupPromotions, defaultSignupPromotion } = signupPromotionsQuery
const { isExisting } = useUserInfo()

// The page prefetches signup offers without awaiting them; only this component waits for them.
onServerPrefetch(async () => {
    await signupPromotionsQuery
})

const showFullTerms = computed(() => props.forceFullTerms || isFromPartner.value)

function promotionTerms(promotion: SignupOffer) {
    if (!showFullTerms.value) return promotion.shortTerms

    // Terms are combined on landingPages to make sure we are total and absolutly compliant!-_-!
    // An explicit `forceFullTerms` opts out of the combine and shows the long terms only.
    if (props.isLandingPage && !props.forceFullTerms) return `${promotion.shortTerms}<br><br>${promotion.longTerms}`

    return promotion.longTerms
}

const terms = computed<OfferTermsContent[]>(() => {
    const termsArray: OfferTermsContent[] = []
    const heroData = props.heroData

    if (heroData?.short_terms || heroData?.long_terms) {
        const heroTerms1 = showFullTerms.value
            ? heroData.long_terms || heroData.short_terms
            : heroData.short_terms
        const heroTerms2 = showFullTerms.value
            ? heroData.long_terms_2 || heroData.short_terms_2
            : heroData.short_terms_2

        if (heroTerms1) termsArray.push({ label: heroData.label, text: heroTerms1 })
        if (heroTerms2) termsArray.push({ label: heroData.label_2, text: heroTerms2 })
    }

    // This makes sure that terms are only shown in game heroes with short_/long_terms (when not from partner or forced)
    if (heroData?.design === 'game' && (!showFullTerms.value || isExisting.value)) {
        return termsArray
    }

    if (!termsArray.length && signupPromotions.value?.length) {
        if (props.enableMultiple) {
            signupPromotions.value.forEach((promotion) => {
                termsArray.push({ label: promotion.label, title: promotion.title, text: promotionTerms(promotion) })
            })

            return termsArray
        }

        if (!defaultSignupPromotion.value) return termsArray

        termsArray.push({ label: defaultSignupPromotion.value.label, text: promotionTerms(defaultSignupPromotion.value) })
    }

    return termsArray
})

// A single terms item has no sibling to disambiguate the promotion's own label.
// Only when `showTitle` is set does the StickyTerms `title` prop take over as the
// heading and override it; otherwise the heading stays hidden, as before.
const displayTerms = computed<OfferTermsContent[]>(() => {
    if (terms.value.length !== 1) return terms.value

    return terms.value.map(({ text }) => ({ text, label: props.showTitle ? props.title : undefined }))
})
</script>

<template>
    <PromotionTerms
        v-if="displayTerms.length"
        class="sticky-terms"
        :class="{
            'container': !fluid,
            'sticky-terms--landing-page': isLandingPage,
            'sticky-terms--fluid': fluid,
        }"
        :terms="displayTerms"
        :title="title"
        :show-title="showTitle"
        :type="showFullTerms ? 'long' : 'short'"
        :text-color="textColor"
        :sticky-config="stickyConfig"
        :style="[resetMargin ? { marginTop: 0 } : null, backgroundColor ? { backgroundColor } : null]"
    />
</template>

<style scoped lang="scss">
.sticky-terms {
    color: var(--sticky-terms-color, var(--on-background));
    z-index: 6;

    :deep(.offer-terms__text) {
        line-height: 1.4;
    }

    &--landing-page {
        color: $white;
        padding-bottom: 1rem;
        background-color: transparent;
        margin-left: auto;
        margin-right: auto;
        max-width: 1024px;
        padding-left: 15px;
        padding-right: 15px;

        :deep(.offer-terms__text b) {
            display: block;
            font-weight: bold;
        }

        :deep(.offer-terms__text a) {
            color: $white;
        }

        :deep(.offer-terms__text) {
            line-height: 1.2;
        }

        :deep(h2) {
            font-size: 16px;
            font-weight: 700;
        }

        :deep(h3) {
            font-size: 14px;
            font-weight: 700;
        }
    }

    // Full-bleed band: element spans edge-to-edge, legal text stays capped.
    // Declared after --landing-page so it wins on the shared width properties.
    &--fluid {
        max-width: none;
        width: 100%;
        margin-left: 0;
        margin-right: 0;
        padding-left: 0;
        padding-right: 0;

        :deep(.offer-terms__text) {
            max-width: 1024px;
            margin-left: auto;
            margin-right: auto;
            padding-left: 15px;
            padding-right: 15px;
        }
    }
}
</style>
